import type { BandProfile, Expr, Profile } from '../core/types';
import { scaleOffsetFor } from '../core/domain';
import type { State } from '../state/store';
import { GLSL_SAFE, SN, lit } from './glsl';
import { RAMPS } from './ramps';
import { chanClass, classBreaks } from './classes';
import { indexById } from './indices';
import { parseIndexExpr } from './parser';
import { classMean, samClassifyExpr, type ClassifyResult } from './sam';

export interface StyleResult {
  color: Expr;
  variables: Record<string, number>;
}

export const bandFor = (p: Profile, role: string): BandProfile | undefined =>
  p.bands.find((b) => b.role === role);
export const nirBand = (p: Profile) => bandFor(p, 'nir') ?? bandFor(p, 'nir08');
export const reBand = (p: Profile) => bandFor(p, 'rededge') ?? bandFor(p, 'rededge1');

/**
 * Stored sample to physical units, as an expression.
 * A scale below 1e-6 has no safe decimal literal, but its reciprocal does — dividing
 * by 1/s is the same operation and compiles.
 */
export function physExpr(p: Profile, b: BandProfile | undefined): Expr | null {
  if (!b) return null;
  const [s, o] = scaleOffsetFor(p.domain, b.index, b.role);
  let e: Expr = ['band', b.glslIndex];   // 1-based: OpenLayers band indices start at 1
  if (s !== 1) {
    if (GLSL_SAFE(s)) e = ['*', e, s];
    else if (GLSL_SAFE(1 / s)) e = ['/', e, 1 / s];
  }
  if (o !== 0 && GLSL_SAFE(o)) e = ['+', e, o];
  return e;
}
/** JavaScript twin of physExpr, for readouts, histograms and auto-stretch. */
export function physValue(p: Profile, bandIndex: number, v: number): number {
  const b = p.bands[bandIndex];
  const [s, o] = scaleOffsetFor(p.domain, bandIndex, b?.role ?? 'other');
  return v * s + o;
}

/**
 * Transparency. Two independent tests, because it is not knowable from here which one
 * OpenLayers leaves intact:
 *   value — test the band against the sentinel. A threshold halfway between the
 *           sentinel and the real data survives texture precision loss and resampling
 *           far better than float equality.
 *   alpha — OpenLayers appends an alpha band when a source declares nodata, and may
 *           zero the data band at the same time, defeating any value test.
 */
export function alphaFor(
  p: Profile, state: State, bands: (BandProfile | undefined)[], olAlphaBand: number | null,
): Expr {
  const terms: Expr[] = [];
  const nd = p.raster.nodata;
  const bs = bands.filter(Boolean) as BandProfile[];
  const { maskMode } = state;

  if (maskMode !== 'off' && maskMode !== 'alpha' && nd != null && bs.length) {
    let test: ((b: BandProfile) => Expr) | null = null;
    const st = bs.map((b) => b.stats).filter((s) => s && !s.empty);
    if (st.length) {
      const lo = Math.min(...st.map((s) => s.min));
      const hi = Math.max(...st.map((s) => s.max));
      const gap = Math.max(hi - lo, Math.abs(hi), Math.abs(lo), 1) * 0.02;
      if (nd < lo - gap) {
        const t = lit((nd + lo) / 2);
        if (t !== null) test = (b) => ['>', ['band', b.glslIndex], t];
      } else if (nd > hi + gap) {
        const t = lit((nd + hi) / 2);
        if (t !== null) test = (b) => ['<', ['band', b.glslIndex], t];
      }
    }
    // No clear gap — the sentinel sits inside the data range, so only equality is safe.
    if (!test) {
      test = GLSL_SAFE(nd)
        ? (b) => ['!=', ['band', b.glslIndex], nd]
        : (b) => ['<', ['abs', ['band', b.glslIndex]], 1e20];
    }
    bs.forEach((b) => terms.push(test!(b)));
  }
  if (maskMode !== 'off' && maskMode !== 'value' && olAlphaBand) {
    terms.push(['>', ['band', olAlphaBand], 0]);
  }
  if (!terms.length) return 1;
  return ['case', terms.length === 1 ? terms[0]! : ['all', ...terms], 1, 0];
}

/**
 * Premultiply. Under premultiplied-alpha blending an alpha of 0 with non-zero RGB
 * still paints at full strength — which looks exactly like the mask being ignored.
 * Multiplying through is correct under either convention and a no-op when alpha is 1.
 */
export const withAlpha = (r: Expr, g: Expr, b: Expr, a: Expr): Expr =>
  a === 1 ? ['color', r, g, b, 1] : ['color', ['*', r, a], ['*', g, a], ['*', b, a], a];

/** lo/hi/igamma are uniforms, so sliders update without recompiling the shader. */
const stretch = (e: Expr): Expr => ['*', 255,
  ['^', ['clamp', ['/', ['-', e, ['var', 'lo']], ['-', ['var', 'hi'], ['var', 'lo']]], 0, 1],
    ['var', 'igamma']]];

/** Ramp position uses the anchors, which may be pinned independently of the window. */
const norm = (e: Expr): Expr =>
  ['clamp', ['/', ['-', e, ['var', 'rlo']], ['-', ['var', 'rhi'], ['var', 'rlo']]], 0, 1];

const chan = (t: Expr, stops: [number, [number, number, number]][], k: number): Expr =>
  ['interpolate', ['linear'], t, ...stops.flatMap(([p, c]) => [p, c[k]!] as Expr[])];

/**
 * Each channel normalised by its own 2–98 range. Sharing one range across R, G and B
 * turns any band-to-band brightness difference into a colour cast — blue is far darker
 * than red over vegetation, so a shared stretch always skews warm.
 */
function perBandNorm(p: Profile, b: BandProfile): Expr {
  const p2 = SN(physValue(p, b.index, b.stats.p2), 0);
  const p98 = SN(physValue(p, b.index, b.stats.p98), 1);
  return ['/', ['-', physExpr(p, b)!, p2], SN(Math.max(p98 - p2, 1e-6), 1)];
}

export const anchors = (s: State): [number, number] =>
  s.lockRamp && s.rampLo != null ? [s.rampLo, s.rampHi!] : [s.lo, s.hi];

export function styleVars(s: State): Record<string, number> {
  const [rlo, rhi] = anchors(s);
  return { lo: s.lo, hi: s.hi, rlo, rhi, igamma: 1 / s.gamma };
}

/** Classification paints class colours, so the ramp and stretch controls do not apply. */
export const isClassifying = (s: State) => s.mode === 'index' && s.indexId === 'sam';
export const canClip = (s: State) =>
  (s.mode === 'index' || s.mode === 'single') && !isClassifying(s);
/**
 * Whether the file can produce a true-colour backdrop at all. This governs whether the
 * layer is *created*, and depends only on the raster — the map is built once per
 * raster, so a layer created conditionally on state that starts false would never
 * exist, and toggling it later would do nothing.
 */
export const canTrueColour = (p: Profile): boolean =>
  !!(bandFor(p, 'red') && bandFor(p, 'green') && bandFor(p, 'blue'));

/**
 * Whether that backdrop should currently be drawn. This governs *visibility* and may
 * change at any time. Classification composites its own backdrop inside the main
 * style, so the separate layer stays hidden there.
 */
export const underlayVisible = (s: State): boolean =>
  s.overTrue && !isClassifying(s);

/**
 * The same raster drawn as true colour, for an index to sit on top of. Its own fixed
 * 2-98 stretch per band, independent of the index stretch above it — otherwise moving
 * the index sliders would drag the reference imagery around with them.
 */
export function buildTrueColourStyle(
  p: Profile, s: State, olAlphaBand: number | null = null,
): StyleResult | null {
  const R = bandFor(p, 'red'), G = bandFor(p, 'green'), B = bandFor(p, 'blue');
  if (!R || !G || !B) return null;
  const d = (b: BandProfile): Expr =>
    ['*', 255, ['^', ['clamp', perBandNorm(p, b), 0, 1], SN(1 / s.gamma, 0.4545)]];
  return {
    color: withAlpha(d(R), d(G), d(B), alphaFor(p, s, [R, G, B], olAlphaBand)),
    variables: {},
  };
}
export const canBalance = (s: State) => s.mode === 'rgb' || s.mode === 'cir' || s.mode === 'custom';
export const canLock = canClip;

/** Locking implies filtering, or narrowing the window would do nothing at all. */
const inRange = (e: Expr): Expr =>
  ['all', ['>=', e, ['var', 'lo']], ['<=', e, ['var', 'hi']]];
const clipped = (s: State, a: Expr, e: Expr): Expr =>
  (s.clipRange || s.lockRamp) ? ['*', a, ['case', inRange(e), 1, 0]] : a;

/** Band names usable inside a custom expression: assigned roles, plus b1..bN. */
export function resolveBand(p: Profile, name: string): Expr | null {
  const n = String(name).toLowerCase();
  const m = /^b(\d+)$/.exec(n);
  if (m) { const b = p.bands[+m[1]! - 1]; return b ? physExpr(p, b) : null; }
  if (n === 'nir') return physExpr(p, nirBand(p));
  if (n === 'rededge') return physExpr(p, reBand(p));
  const b = p.bands.find((x) => x.role === n);
  return b ? physExpr(p, b) : null;
}

/** Fallback backdrop band: the first spectral band, or failing that any real band. */
export const greyBand = (p: Profile): BandProfile | undefined =>
  p.bands.find((b) => b.wavelengthNm != null && b.role !== 'pan')
  ?? p.bands.find((b) => b.role !== 'alpha');

export const samBands = (p: Profile): BandProfile[] =>
  p.bands.filter((b) => b.wavelengthNm != null && b.role !== 'pan');

/** The raw index expression, before any stretch. Null with a reason if unavailable. */
export function indexExpr(
  p: Profile, s: State,
): { expr: Expr | null; error: string | null } {
  const ix = indexById(s.indexId);
  if (!ix) return { expr: null, error: 'Unknown index' };
  if (ix.sam) {
    // Classification produces a class index, not a continuous value. The scalar form
    // exists so the histogram and export machinery still have something to read.
    const r = samClassify(p, s);
    if (!r) return { expr: null, error: 'Define a class and click the map to train it' };
    return { expr: ['case', r.alpha, r.winner, -1], error: null };
  }
  if (ix.custom) {
    const r = parseIndexExpr(s.customSrc, (n) => resolveBand(p, n));
    return r.ok ? { expr: r.node, error: null } : { expr: null, error: r.error };
  }
  const B = {
    red: physExpr(p, bandFor(p, 'red'))!, green: physExpr(p, bandFor(p, 'green'))!,
    blue: physExpr(p, bandFor(p, 'blue'))!, nir: physExpr(p, nirBand(p))!,
    rededge: physExpr(p, reBand(p))!,
  };
  if (ix.needs.some((n) => !B[n as keyof typeof B])) {
    return { expr: null, error: `needs ${ix.needs.join(', ')}` };
  }
  return { expr: ix.calc!(B), error: null };
}

/** Classes with enough training data to contribute a spectrum. */
export const trainedClasses = (s: State) =>
  s.classes.filter((c) => classMean(c.points) != null);

/** The classifier for the current training set, or null if it cannot be built yet. */
export function samClassify(p: Profile, s: State): ClassifyResult | null {
  return samClassifyExpr(s.classes, (bi) => physExpr(p, p.bands[bi]), s.samMaxAngle);
}

export function indexBands(p: Profile, s: State): (BandProfile | undefined)[] {
  const ix = indexById(s.indexId);
  if (ix?.sam) return samBands(p);
  if (!ix || ix.custom) return p.bands.filter((b) => b.role !== 'alpha');
  return ix.needs.map((n) =>
    n === 'nir' ? nirBand(p) : n === 'rededge' ? reBand(p) : bandFor(p, n));
}

export function compositeBands(p: Profile, s: State): (BandProfile | undefined)[] {
  if (s.mode === 'rgb') return ['red', 'green', 'blue'].map((r) => bandFor(p, r));
  if (s.mode === 'cir') return [nirBand(p), bandFor(p, 'red'), bandFor(p, 'green')];
  return s.customBands.map((i) => p.bands[i]);
}

/**
 * Balanced mode already normalises each band to roughly 0–1, so the sliders work in
 * that space. Unbalanced mode needs real data values, and a hardcoded 0–0.3 is only
 * right if the file really is 0–1 reflectance — derive it from the data instead.
 */
export function compositeDefaults(p: Profile, s: State): [number, number] {
  if (s.balanceBands) return [0, 1];
  const ok = compositeBands(p, s).filter(Boolean) as BandProfile[];
  if (!ok.length) return [0, 1];
  return [
    Math.min(...ok.map((b) => physValue(p, b.index, b.stats.p2))),
    Math.max(...ok.map((b) => physValue(p, b.index, b.stats.p98))),
  ];
}

/**
 * Channel expressions for whatever sits underneath — true colour, CIR, or any index
 * with its own ramp. Classification needs something visible to click on, and for
 * vegetation work an NDVI backdrop is far more useful than RGB.
 *
 * Returns 0-255 per channel, with its own stretch, so the index sliders above it do
 * not drag the backdrop around.
 */
export function underlayChannels(p: Profile, s: State): ((k: number) => Expr) | null {
  const gamma = SN(1 / s.gamma, 0.4545);
  const shade = (b: BandProfile | undefined): Expr | null =>
    b ? ['*', 255, ['^', ['clamp', perBandNorm(p, b), 0, 1], gamma]] : null;

  if (s.underlay === 'true' || s.underlay === 'cir' || s.underlay === 'grey') {
    if (s.underlay !== 'grey') {
      const bs = s.underlay === 'cir'
        ? [nirBand(p), bandFor(p, 'red'), bandFor(p, 'green')]
        : [bandFor(p, 'red'), bandFor(p, 'green'), bandFor(p, 'blue')];
      const ch = bs.map(shade);
      if (ch.every((c) => c != null)) return (k) => ch[k]!;
    }
    // Without three named colour bands there is no true colour to show, and a blank
    // map is unusable for training. One band in grey is still something to click on.
    const g = shade(greyBand(p));
    return g ? () => g : null;
  }

  const ix = indexById(s.underlay);
  if (!ix?.calc) return null;
  const { expr } = indexExpr(p, { ...s, indexId: s.underlay });
  if (!expr) return null;
  const stops = RAMPS[ix.ramp]?.stops ?? RAMPS['rdylgn']!.stops;
  const [lo, hi] = ix.range;
  const t: Expr = ['clamp', ['/', ['-', expr, SN(lo, 0)], SN(hi - lo, 1)], 0, 1];
  return (k) => chan(t, stops, k);
}

export function buildStyle(
  p: Profile, s: State, olAlphaBand: number | null = null,
): StyleResult {
  const vars = styleVars(s);
  const stops = RAMPS[s.rampId]?.stops ?? RAMPS['rdylgn']!.stops;

  if (s.mode === 'index') {
    // Classification paints class colours over the backdrop — no ramp, no stretch.
    // Compositing here rather than stacking a second layer halves the tile requests,
    // which is what the source is actually rate-limited on.
    if (indexById(s.indexId)?.sam) {
      const under = underlayChannels(p, s);
      const r = samClassify(p, s);
      const base = alphaFor(p, s, samBands(p), olAlphaBand);
      if (!r) {
        // Nothing trained yet: show the backdrop so there is something to click on.
        if (!under) return { color: ['color', 0, 0, 0, 0], variables: vars };
        return { color: withAlpha(under(0), under(1), under(2), base), variables: vars };
      }
      const mix = SN(Math.min(1, Math.max(0, s.opacity)), 1);
      const ch = (k: number): Expr => {
        const cls = r.chan(k);
        if (!under) return cls;
        const blended: Expr = ['+', ['*', cls, mix], ['*', under(k), 1 - mix]];
        return ['case', ['>=', r.alpha, 1], blended, under(k)];
      };
      const a: Expr = under ? base : ['*', r.alpha, base];
      return { color: withAlpha(ch(0), ch(1), ch(2), a), variables: vars };
    }
    const { expr } = indexExpr(p, s);
    if (expr == null) return { color: ['color', 0, 0, 0, 0], variables: vars };
    const t = norm(expr);
    const breaks = classBreaks(s.classN, s.classMethod);
    const ch = s.classify
      ? (k: number) => chanClass(t, stops, breaks, s.classN, k)
      : (k: number) => chan(t, stops, k);
    let a = clipped(s, alphaFor(p, s, indexBands(p, s), olAlphaBand), expr);
    // A value outside the index's physical bounds means the denominator crossed zero.
    // Painting it would show open water as the most vigorous ground in the scene.
    const bounds = indexById(s.indexId)?.bounds;
    if (bounds) {
      a = ['*', a, ['case',
        ['all', ['>=', expr, SN(bounds[0], -1)], ['<=', expr, SN(bounds[1], 1)]], 1, 0]];
    }
    return { color: withAlpha(ch(0), ch(1), ch(2), a), variables: vars };
  }

  if (s.mode === 'rgb' || s.mode === 'cir' || s.mode === 'custom') {
    const bs = compositeBands(p, s);
    if (bs.some((b) => !b)) return { color: ['color', 0, 0, 0, 0], variables: vars };
    const ce = (b: BandProfile) => stretch(s.balanceBands ? perBandNorm(p, b) : physExpr(p, b)!);
    const a = alphaFor(p, s, bs, olAlphaBand);
    return {
      color: withAlpha(ce(bs[0]!), ce(bs[1]!), ce(bs[2]!), a),
      variables: vars,
    };
  }

  const b = p.bands[s.singleBand];
  if (!b) return { color: ['color', 0, 0, 0, 0], variables: vars };
  const e = physExpr(p, b)!;
  const t = norm(e);
  const breaks = classBreaks(s.classN, s.classMethod);
  const ch = s.classify
    ? (k: number) => chanClass(t, stops, breaks, s.classN, k)
    : (k: number) => chan(t, stops, k);
  const a = clipped(s, alphaFor(p, s, [b], olAlphaBand), e);
  return { color: withAlpha(ch(0), ch(1), ch(2), a), variables: vars };
}
